import { takeLatest, put, all, call, fork, select } from "redux-saga/effects";
import { getItem, setItem, getData } from "@lib/storage";
import { actions } from "@reducers/configReducer";
import { actions as authActions } from "@reducers/authReducer";
import { initRemoteConfig } from "@services/firebase";
import { actions as categoryActions } from "@reducers/categoriesReducer";
import { actions as refreshActions } from "@reducers/refreshReducer";
import { actions as cartActions } from "@reducers/cartReducer";
import { actions as supportActions } from "@reducers/supportReducer";
import { actions as homeWidgetActions } from "@reducers/homeWidgetReducer";
import setAppConfigs from "@lib/setAppConfigs";
import {
  getDropDownOptions,
  getAppSettings,
  getAppBootParams,
} from "@services/api";
import { AppEventsLogger } from "react-native-fbsdk-next";
import crashlytics from "@react-native-firebase/crashlytics";
import * as Sentry from "@sentry/react-native";
import analytics from "@services/analytics";
import clTaskAction from "@actions/clTask.action";
import notificationPermission from "@lib/logNotificationPermission";
import getTokenZ from "@utils/getTokenZ";
import { getHomePageNew } from "@services/cmsV2-api";
import {
  GET_APP_SETTINGS,
  SET_APP_SETTINGS,
  GET_APP_BOOT_PARAMS,
  SYNC_USER_ACTIONS,
  SET_LANGUAGE,
  INVOKE_APP_SETUP,
  GET_USER_DETAILS_BY_TOKEN_REQUEST,
  CLEAR_APP_SETUP,
  SET_APPLINK,
} from "../reducers/actionTypes";
import { configureApplinks, setAppLink } from "./configureApplinks";
import trackVisibleApps from "./utils/appVisibilityTracker";
// Plotline removed

// Notification channels ("testchannel", "cl_pn_ring_pop_up") are created
// natively in MainApplication.onCreate — no JS-side channel setup needed.

export const setupCrashlytics = ({ userId, attributes }) => {
  return Promise.all([
    crashlytics().setUserId(userId),
    crashlytics().setAttributes(attributes),
  ]);
};

export function* setSentryCrashlytics(data) {
  // replace call -> fork non blocking, limit these calls..
  // if (!data.name) return;
  yield call(Sentry.setUser, {
    id: data.id,
    username: data.name,
  });
  yield call(analytics.setUser, data.id, data);
  yield call(AppEventsLogger.setUserID, data.id);

  yield call(setupCrashlytics, {
    userId: data.id,
    attributes: {
      user_name: String(data.name),
      phone_number: String(data.phone_number),
    },
  });

  // add context for sentry here....
  const otherInfo = {
    phone_number: data.phone_number,
    version_code: data.version_code,
    version_name: data.version_name,
  };
  Sentry.setContext("otherInfo", otherInfo);
}

function* invokeAppSetup() {
  try {
    /* setup channel subscription */
    yield fork(configureApplinks);
    yield call(fetchSyncUser);

    yield put({ type: "INVOKE_COMPLETE" });
  } catch (e) {
    yield put(actions.failureSyncUser(e));
  }
}

function* clearAppSetup() {
  // Perform app unmounting cleanup actions here
  yield "cleanup complete";
  console.log("clearAppSetup");
}

function* getAppSettingsSaga() {
  try {
    const { data } = yield call(getAppSettings);
    if (data) {
      yield put({
        type: SET_APP_SETTINGS,
        payload: data,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

function* setupRemoteConfig() {
  yield fork(initRemoteConfig);
}

function* getAppBootParamsSaga() {
  try {
    const { data } = yield call(getAppBootParams);
    if (data) {
      yield put({
        type: SET_APP_SETTINGS,
        payload: data.appSettings,
      });
    }
  } catch (error) {
    console.log(error);
  }
}

function* setBootParams(data) {
  const {
    ratingCheck,
    version,
    homePage,
    checkClOrderRating,
    banners,
    oosSku,
    appSettings,
    onboardingSupportNumber,
    currentMonthStats,
    categoryList = [],
    fullCategoryList = {},
    ReplaceReasons,
    ReturnReasons,
    feedStaleDelayMs,
    todayDailyTasks,
    loggedInDevicesSummary,
    isNewShopPerformanceEnabled,
    isStarCp,
    isStarCpHasActiveDbs,
    isStarCpHasActiveRoutesV2,
    isVacationEnabled,
    isSortingEnabled,
    isWalletEnabled,
    partialDeliveryEnabled,
    multiOrderDelivery,
    disable_add_db_btn,
    pollingEnable,
    pollingInterval,
    features = {},
  } = data;

  yield put(
    actions.successSyncUser({
      ratingCheck,
      version,
      homePage,
      checkClOrderRating,
      banners,
      oosSku,
      appSettings,
      onboardingSupportNumber,
      currentMonthStats,
      ReplaceReasons,
      ReturnReasons,
      categories: fullCategoryList.result || [],
      isNewShopPerformanceEnabled,
      isStarCp,
      isStarCpHasActiveDbs,
      isStarCpHasActiveRoutesV2,
      isVacationEnabled,
      isSortingEnabled,
      isWalletEnabled,
      partialDeliveryEnabled,
      multiOrderDelivery,
      disable_add_db_btn,
      pollingEnable,
      pollingInterval,
      features,
    }),
  );

  if (loggedInDevicesSummary) {
    yield put(authActions.setLoggedInOptions(loggedInDevicesSummary));
  }

  if (todayDailyTasks) {
    yield put(clTaskAction.getTodayDailyTasks.success(todayDailyTasks, null));
  } else {
    yield put(clTaskAction.getTodayDailyTasks.success({}, null));
  }

  if (categoryList) {
    yield put(
      categoryActions.gegetAllPrimaryCategoriesWithProductTypesSuccess(
        categoryList,
      ),
    );
  }

  yield call(setupRemoteConfig);
  if (feedStaleDelayMs) {
    yield put(refreshActions.setDelayDuration(feedStaleDelayMs));
  }
}

function* fetchSyncUser({ payload: { userToken, bootParams } = {} } = {}) {
  try {
    const token = userToken || (yield call(getTokenZ));

    const bootParamsData = bootParams || (yield call(getAppBootParams)).data;
    const homePage = (yield call(getHomePageNew, {
      limit: 10,
      offset: 0,
    })).data;
    bootParamsData.homePage = homePage;
    yield fork(setBootParams, bootParamsData);
    if (bootParamsData.meResponse) {
      if (token) {
        const {
          data: { supportTicket, clDropDownOptions },
        } = yield call(getDropDownOptions);
        yield put(
          authActions.setTeamLeaderDetails({
            ...bootParamsData.meResponse,
            supportTicket,
            clDropDownOptions,
          }),
        );
        // Plotline initialization removed
      }

      const is_suspended =
        bootParamsData?.meResponse?.is_suspended ||
        bootParamsData?.meResponse?.leader_state === "LEADER_SUSPENDED";
      if (is_suspended) {
        yield put(authActions.showSuspensionModal(is_suspended || false));
        yield put(supportActions.getSupportConfigs());
      }
      yield fork(setSentryCrashlytics, bootParamsData?.meResponse);
    } else if (token) {
      yield put({ type: GET_USER_DETAILS_BY_TOKEN_REQUEST });
    }
    let selectedContact = yield select(
      ({ customer }) => customer?.selectedContact,
    );
    if (!selectedContact) {
      selectedContact = yield call(getData, "selectedContact");
    }
    if (selectedContact && token) {
      yield put(cartActions.viewCartLiteRequest(selectedContact));
    }
    yield put(homeWidgetActions.fetchHomePageWidget());

    /* below code should be non blocking */

    yield fork(function* () {
      const isSignupSessionVideo =
        yield getItem("signupSessionVideo");
      if (isSignupSessionVideo) {
        yield setItem("startSession", "true");
      }

      const value = yield getItem("cmNumberViewCount");
      const NUM_TIMES_WA_NUMBER_PROMPT_VISIBLE = yield select(
        (state) => state?.configReducer?.NUM_TIMES_WA_NUMBER_PROMPT_VISIBLE,
      );
      if (!value) {
        yield setItem("cmNumberViewCount", "1");
      } else if (parseInt(value, 10) < NUM_TIMES_WA_NUMBER_PROMPT_VISIBLE) {
        yield setItem(
          "cmNumberViewCount",
          `${parseInt(value, 10) + 1}`,
        );
      } else {
        yield setItem("cmNumberViewCount", "999");
      }
    });
    yield put(authActions.restoreToken({ token }));
    yield put(authActions.setFetchMeCompleted());
    yield fork(setAppConfigs); // call after token is saved in the redux
    yield fork(notificationPermission);
    yield call(trackVisibleApps);
  } catch (e) {
    Sentry.captureException(e);
    console.log("sync failed", e);
    yield put(actions.failureSyncUser(e));
  }
}

export default function* root() {
  yield all([
    takeLatest(INVOKE_APP_SETUP, invokeAppSetup),
    takeLatest(GET_APP_SETTINGS, getAppSettingsSaga),
    takeLatest(GET_APP_BOOT_PARAMS, getAppBootParamsSaga),
    takeLatest(SYNC_USER_ACTIONS.FETCH_SYNC_USER, fetchSyncUser),
    takeLatest(SET_LANGUAGE, fetchSyncUser),
    takeLatest(CLEAR_APP_SETUP, clearAppSetup),
    takeLatest(SET_APPLINK, setAppLink),
  ]);
}
